import { useLoad } from '@/hooks/load/useLoad'
import { useLoadLanguages } from '@/hooks/load/useLoadLanguages'
import { useStringParam } from '@/hooks/useParam'
import { getConfigLanguages } from '@/stores/config'
import { useSelector } from '@/stores/hooks'
import { getLanguageData } from '@/stores/languages'
import { loadLanguage } from '@/stores/load/loadLanguage'
import { matchLanguage } from '@/util/translate/languages'
import { Translator } from '@/util/translate/translator'
import type { Children } from '@lc/libreact'
import { eq } from '@lc/libutils'
import { createContext, useEffect, useMemo, useState } from 'react'

// eslint-disable-next-line react-refresh/only-export-components
export const TranslatorContext = createContext<Translator | null>(null)
TranslatorContext.displayName = 'TranslatorContext'

export type LangListener = (lang: string) => void

export function TranslatorProvider({
	children,
	collectLang,
	ssrLang,
	ssrLangs,
}: {
	children: Children
	collectLang?: LangListener | null
	ssrLang?: string
	ssrLangs?: string[]
}) {
	const [language, setLanguage] = useState(ssrLang ?? '')
	const languages = useSelector(getConfigLanguages)
	const [requestedLang] = useStringParam('lang', '')
	const [navigatorLanguages, setNavigatorLanguages] = useState(ssrLangs ?? [])
	const languageData = useSelector(state =>
		language ? getLanguageData(state, language) : undefined,
	)

	useLoadLanguages({ level: 0 })

	useEffect(() => {
		// on browser load, switch to the browser language list
		if (!eq(navigator.languages, navigatorLanguages)) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setNavigatorLanguages([...navigator.languages])
		}
	}, [navigatorLanguages])

	const matchedLanguage = useMemo(() => {
		// detect the language we want
		if (!languages) return ''
		return matchLanguage(languages, navigatorLanguages, requestedLang)
	}, [languages, navigatorLanguages, requestedLang])

	// load the language we matched
	const { status } = useLoad(loadLanguage, {
		level: 0,
		disable: !matchedLanguage,
		id: matchedLanguage,
	})
	// once it's loaded, set it to be active
	if (status === 'loaded' && matchedLanguage !== language)
		setLanguage(matchedLanguage)

	// forward the language to the ssr process
	if (language && languageData && collectLang) collectLang(language)

	// build our translator object from the language
	const translator = useMemo(() => {
		if (!language) return null
		if (!languageData) return null
		return new Translator(languageData)
	}, [language])

	useEffect(() => {
		// bind the language to the <html lang>
		window.document.documentElement.lang = language
	}, [language])

	if (!translator) return null

	return <TranslatorContext value={translator}>{children}</TranslatorContext>
}
